import { useMemo, useState } from "react";
import type { InstanceMember, InstancePerson } from "@keydials/api-client";
import { ApiError } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";
import { SettingsCard } from "./SettingsCard";
import { memberRows } from "./memberRows";

/**
 * The dial's Who's who (KEY-PEOPLE): the people this dial keeps track of — a family's children, a home's
 * residents — whether or not they can sign in. A Person field on the dial picks from this list, so
 * "Dentist: for Mila" is answered from a name typed here once.
 *
 * Distinct from the People tab, which is who can OPEN the dial. The two meet through the link: a person here
 * may be linked to one of those members (a child given a Reader account of their own), and from then on the
 * records naming the person and the account that reads the dial are one and the same. Only a dial admin
 * writes here; the server holds the same line.
 */
export function WhosWhoSection({
  instanceId,
  people,
  members,
  canManage,
  onChanged,
}: {
  instanceId: string;
  people: InstancePerson[];
  members: InstanceMember[];
  canManage: boolean;
  onChanged: () => void;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; name: string; userId: string } | null>(null);

  // One row per account, archived ones left out — the same fold the People tab draws — for the link picker.
  const linkable = useMemo(
    () => memberRows(members, { guestUserIds: new Set(), canGrantAdmin: false }).filter((r) => !r.isGuest),
    [members],
  );
  const sorted = useMemo(() => [...people].sort((a, b) => a.order - b.order), [people]);

  async function run(work: (token: string) => Promise<void>) {
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await work(token);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError && e.message ? e.message : "That didn’t save. Try again.");
    } finally {
      setBusy(false);
    }
  }

  const add = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    void run(async (token) => {
      await api.addInstancePerson(token, instanceId, { name: trimmed });
      setName("");
    });
  };

  const saveEdit = () => {
    if (!editing) return;
    const draft = editing;
    void run(async (token) => {
      await api.updateInstancePerson(token, instanceId, draft.id, { name: draft.name.trim(), userId: draft.userId || null });
      setEditing(null);
    });
  };

  const remove = (person: InstancePerson) => {
    if (!window.confirm(`Take ${person.name} off the list? Anything already recorded against them is kept.`)) return;
    void run((token) => api.removeInstancePerson(token, instanceId, person.id));
  };

  const move = (index: number, delta: number) => {
    const next = [...sorted];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    void run((token) => api.reorderInstancePeople(token, instanceId, next.map((p) => p.id)));
  };

  const accountLabel = (p: InstancePerson) =>
    p.userId ? (p.userDisplayName?.trim() || p.userEmail || "a member") + (p.archived ? " · archived" : "") : null;

  return (
    <SettingsCard
      title="Who’s who"
      subtitle="The people this dial keeps track of, whether or not they can sign in. Fields on the dial that ask “who?” pick from this list. Link a name to someone on the People tab when they have an account of their own."
    >
      {sorted.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13.5, color: "var(--text-muted)" }}>Nobody on the list yet.</p>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column" }}>
          {sorted.map((p, i) => (
            <li key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: i === 0 ? "none" : "var(--border-w) solid var(--border)", flexWrap: "wrap" }}>
              {editing?.id === p.id ? (
                <>
                  <input
                    className="field"
                    aria-label="Name"
                    value={editing.name}
                    maxLength={120}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); }}
                    style={{ flex: "1 1 160px", minWidth: 0 }}
                  />
                  <select
                    className="field"
                    aria-label="Linked account"
                    value={editing.userId}
                    onChange={(e) => setEditing({ ...editing, userId: e.target.value })}
                    style={{ flex: "1 1 200px", minWidth: 0 }}
                  >
                    <option value="">Not linked to an account</option>
                    {linkable
                      .filter((r) => r.userId === editing.userId || !people.some((other) => other.id !== p.id && other.userId === r.userId))
                      .map((r) => <option key={r.userId} value={r.userId}>{r.name}</option>)}
                  </select>
                  <button type="button" className="kd-btn-primary kd-btn-sm" disabled={busy || !editing.name.trim()} onClick={saveEdit}>Save</button>
                  <button type="button" className="kd-btn kd-btn-sm" disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
                </>
              ) : (
                <>
                  <span style={{ flex: "1 1 160px", minWidth: 0, fontSize: 14, color: "var(--text)" }}>
                    {p.name}
                    {accountLabel(p) && (
                      <span style={{ marginLeft: 8, fontSize: 12, color: "var(--text-muted)" }}>· {accountLabel(p)}</span>
                    )}
                  </span>
                  {canManage && (
                    <span style={{ display: "inline-flex", gap: 6 }}>
                      <button type="button" className="kd-btn kd-btn-sm" aria-label={`Move ${p.name} up`} disabled={busy || i === 0} onClick={() => move(i, -1)}>↑</button>
                      <button type="button" className="kd-btn kd-btn-sm" aria-label={`Move ${p.name} down`} disabled={busy || i === sorted.length - 1} onClick={() => move(i, 1)}>↓</button>
                      <button type="button" className="kd-btn kd-btn-sm" disabled={busy} onClick={() => setEditing({ id: p.id, name: p.name, userId: p.userId ?? "" })}>Edit</button>
                      <button type="button" className="kd-btn kd-btn-sm" disabled={busy} onClick={() => remove(p)}>Remove</button>
                    </span>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {canManage && (
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <input
            className="field"
            aria-label="Add a person"
            placeholder="Add a name — a child, a resident, anyone this dial is about"
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
            style={{ flex: "1 1 240px", minWidth: 0 }}
          />
          <button type="button" className="kd-btn-primary" disabled={busy || !name.trim()} onClick={add}>Add</button>
        </div>
      )}
      {error && <p role="alert" style={{ margin: "10px 0 0", fontSize: 13, color: "var(--st-red, #b91c1c)" }}>{error}</p>}
    </SettingsCard>
  );
}
